import { ArrowRight, ArrowLeft, Check, X, Sparkles } from "../../icons";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { Textarea } from "../../textarea";
import { cn } from "../../utils";
import { WhyTrace } from "./why-trace";
import { DarkRoom, BrandLockup } from "./dark-room";
import type {
  SetupShellContext,
  SetupActionCard,
  SetupPromptCard,
} from "../setup/setup-driver";

/**
 * The Play "Screening Room" takeover — the production skin for the Play driver
 * (the `/styleguide/play` Spotlight exploration, Screening Room + Dark Room
 * direction, made real). Rendered via SetupDriver's `renderShell` seam so it
 * reuses the shared walk brain (impressions, optimistic answer save + rollback,
 * keyboard, finish/empty) WITHOUT forking the stepper — it only paints.
 *
 * The takeover is a minimal, near-black room — the app's own dark surface lit by
 * one very soft pool of light from the top (the shared `DarkRoom`), the move's
 * title the one fully-lit object. The real product logo sits top-left; progress
 * speaks film — a "TRACK 03 / 07" cue + a vertical film-strip rail. The whole
 * subtree is scoped `dark` so every atom (Button, Badge, Textarea) renders its
 * dark-theme tokens natively over the room — no per-element repaints.
 *
 * The backdrop + the brand lockup are imported from ./dark-room — the single
 * shared source across the takeover, entrance, login, and onboarding.
 */

/** A vertical "film strip" rail — one frame per card; the lit frame is current. */
function FilmStrip({ index, count }: { index: number; count: number }) {
  return (
    <div className="flex max-h-[58vh] flex-col items-center gap-2 overflow-hidden" aria-hidden>
      {Array.from({ length: count }).map((_, i) => {
        const state = i < index ? "played" : i === index ? "current" : "future";
        return (
          <span
            key={i}
            className={cn(
              "w-1 rounded-full transition-all duration-300 ease-out",
              state === "current"
                ? "h-7 bg-primary shadow-[0_0_12px_color-mix(in_oklab,var(--primary)_60%,transparent)]"
                : state === "played"
                  ? "h-3 bg-white/25"
                  : "h-3 bg-white/12",
            )}
          />
        );
      })}
    </div>
  );
}

/* ── The takeover shell ──────────────────────────────────────────────────── */

export function PlayTakeoverShell({ ctx }: { ctx: SetupShellContext }) {
  const { current, index, count, isEmpty, done, rootRef, onKeyDown } = ctx;
  const pad = String(Math.max(count, 1)).length;
  const midWalk = !done && !isEmpty && current;

  return (
    <div
      ref={rootRef}
      onKeyDown={onKeyDown}
      className="dark relative flex h-full w-full items-center justify-center overflow-hidden text-foreground"
    >
      <DarkRoom />

      {/* back — top-left, only past the first move */}
      {midWalk && ctx.canBack ? (
        <button
          type="button"
          onClick={ctx.back}
          aria-label="Previous"
          className="absolute left-6 top-6 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
        >
          <ArrowLeft strokeWidth={1.5} className="h-4 w-4" />
        </button>
      ) : null}
      {/* close — top-right */}
      <button
        type="button"
        onClick={() => ctx.onExhausted?.()}
        aria-label="Close Play"
        className="absolute right-6 top-6 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
      >
        <X strokeWidth={1.5} className="h-4 w-4" />
      </button>

      {/* the real product logo + TRACK cue */}
      {midWalk ? (
        <div className="absolute left-6 top-16 z-[1] flex items-center gap-2.5">
          <BrandLockup />
          <span className="border-l border-white/15 pl-2.5 type-subtext font-medium uppercase tracking-[0.22em] tabular-nums text-muted-foreground/70">
            Track {String(index + 1).padStart(pad, "0")} / {String(count).padStart(pad, "0")}
          </span>
        </div>
      ) : null}

      {/* film-strip rail — far right, hidden on small */}
      {midWalk ? (
        <div className="absolute right-8 top-1/2 z-[1] hidden -translate-y-1/2 sm:block">
          <FilmStrip index={index} count={count} />
        </div>
      ) : null}

      <div className="relative z-[1] flex w-full max-w-[34rem] flex-col px-8 sm:pr-14">
        {isEmpty ? (
          <EmptyView ctx={ctx} />
        ) : done ? (
          <FinishView ctx={ctx} />
        ) : current ? (
          current.mode === "prompt" ? (
            <PromptMove ctx={ctx} card={current} />
          ) : (
            <ActionMove ctx={ctx} card={current} />
          )
        ) : null}
      </div>
    </div>
  );
}

function ActionMove({ ctx, card }: { ctx: SetupShellContext; card: SetupActionCard }) {
  const reasons = (card.reasons ?? []).slice(0, 2);
  // A Learning Loop memory card (key `learning:<id>`) is KEPT in place, not
  // navigated to: its primary verb posts 'completed' (→ the ledger row lands)
  // through the same signal path as dismiss, rather than 'started'+navigate.
  const isKeep = card.key.startsWith("learning:");
  return (
    <div key={card.key} className="flex flex-col gap-6 duration-150 animate-in fade-in-0">
      {/* kicker — category badge + the one violet accent (stakes) */}
      {card.badge || card.payoff ? (
        <div className="flex items-center gap-3">
          {card.badge ? <Badge variant={card.badge.intent}>{card.badge.label}</Badge> : null}
          {card.payoff ? <span className="type-body font-medium text-primary">{card.payoff}</span> : null}
        </div>
      ) : null}

      {/* the title — the one fully-lit object, in the beam */}
      <h2 className="max-w-[30rem] type-heading font-medium tracking-[-0.02em] text-foreground">
        {card.title}
      </h2>

      {card.body ? (
        <p className="max-w-[32rem] type-body leading-body text-muted-foreground">{card.body}</p>
      ) : null}
      {reasons.length > 0 ? (
        <p className="type-body leading-body text-muted-foreground/60">{reasons.join("  ·  ")}</p>
      ) : null}

      {/* "Why?" — the causal trace (§6.5.8/§12-8). Only event-derived cards carry
          a causationUid; the shared WhyTrace walks /api/events/chain into a
          CausalTraceView. The dark-room panel keeps it native to the takeover's beam. */}
      {card.causationUid ? (
        <WhyTrace
          uid={card.causationUid}
          kind={card.causationKind}
          panelClassName="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-2 duration-150 animate-in fade-in-0"
        />
      ) : null}

      {/* action zone — one full-width primary, then Skip + the secondary verbs */}
      <div className="flex flex-col gap-4 pt-1">
        <Button
          data-setup-focus
          size="lg"
          className="w-full justify-center"
          onClick={() => (isKeep ? ctx.complete() : ctx.onAct ? ctx.onAct(card) : ctx.act())}
        >
          {isKeep ? "Keep this" : (card.ctaLabel ?? "Do it")}
          {isKeep ? (
            <Check strokeWidth={1.5} className="h-4 w-4" />
          ) : (
            <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
          )}
        </Button>
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={ctx.skip}>
            Skip for now
          </Button>
          <div className="flex items-center gap-2">
            {ctx.onDelegate ? (
              <Button variant="ghost" onClick={() => ctx.onDelegate!(card)}>
                Delegate
              </Button>
            ) : null}
            {ctx.onDive && card.canDive ? (
              <Button variant="outline" onClick={() => ctx.onDive!(card)}>
                Dive in
              </Button>
            ) : null}
          </div>
        </div>
        {ctx.showDismiss ? (
          <button
            type="button"
            onClick={ctx.dismiss}
            className="self-center type-subtext text-muted-foreground/50 transition-colors hover:text-muted-foreground"
          >
            Not relevant
          </button>
        ) : null}
      </div>
    </div>
  );
}

function PromptMove({ ctx, card }: { ctx: SetupShellContext; card: SetupPromptCard }) {
  const headline = card.question ?? card.title;
  return (
    <div key={card.key} className="flex flex-col gap-6 duration-150 animate-in fade-in-0">
      <h2 className="max-w-[30rem] type-heading font-medium tracking-[-0.02em] text-foreground">
        {headline}
      </h2>
      {card.sublabel ? (
        <p className="max-w-[32rem] type-body leading-body text-muted-foreground">{card.sublabel}</p>
      ) : null}

      <div className="flex flex-col gap-2">
        {card.renderInput ? (
          card.renderInput({
            value: ctx.value,
            setValue: ctx.setValue,
            save: ctx.save,
            error: ctx.error,
            disabled: false,
          })
        ) : (
          <Textarea
            autosize
            rows={2}
            data-setup-focus
            value={typeof ctx.value === "string" ? ctx.value : ""}
            onChange={(e) => ctx.setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                ctx.save();
              }
            }}
            placeholder={card.placeholder ?? "Type your answer…"}
            aria-label="Your answer"
            className="min-h-24 rounded-xl px-4 py-3 type-body leading-body"
          />
        )}
        {card.footer}
        {ctx.error ? <span className="type-subtext text-destructive">{ctx.error}</span> : null}
      </div>

      <div className="flex items-center justify-between pt-1">
        <Button variant="ghost" onClick={ctx.skip} className="text-muted-foreground">
          Skip for now
        </Button>
        <Button size="lg" onClick={ctx.save} disabled={!ctx.canSave} className="min-w-40 justify-center">
          Save
          <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function FinishView({ ctx }: { ctx: SetupShellContext }) {
  const cleared = ctx.decidedCount;
  const hasMore = ctx.remaining > 0;
  return (
    <div className="flex flex-col items-center gap-5 py-10 text-center duration-300 animate-in fade-in-0 zoom-in-95">
      <Sparkles strokeWidth={1.5} className="h-7 w-7 text-muted-foreground" />
      <div className="flex flex-col gap-1.5">
        <p className="type-heading font-medium text-foreground">
          You moved through {cleared} {cleared === 1 ? "thing" : "things"}
        </p>
        <p className="max-w-[26rem] type-body text-muted-foreground">
          {hasMore
            ? "More is queued whenever you want it."
            : "That's everything pressing right now. New work shows up here as it appears."}
        </p>
      </div>
      <div className="flex items-center gap-2 pt-1">
        {hasMore && ctx.onRestart ? (
          <Button variant="outline" size="lg" onClick={ctx.onRestart}>
            Pick another
          </Button>
        ) : null}
        <Button data-setup-focus size="lg" onClick={() => ctx.onExhausted?.()}>
          Done
        </Button>
      </div>
    </div>
  );
}

function EmptyView({ ctx }: { ctx: SetupShellContext }) {
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center">
      <Sparkles strokeWidth={1.5} className="h-7 w-7 text-muted-foreground" />
      <p className="type-title font-medium text-foreground">You&rsquo;re all caught up</p>
      <p className="max-w-[26rem] type-body text-muted-foreground">
        Nothing pressing right now. New work shows up here as it appears.
      </p>
      <Button data-setup-focus size="lg" className="mt-2" onClick={() => ctx.onExhausted?.()}>
        Done
      </Button>
    </div>
  );
}
